// Self-contained kernel widget. Given a kernel definition (or id) +
// optional params binding, runs the kernel deterministically and
// mounts <KernelBlock> with the result. Handles loading / error /
// refresh states inline so it can be dropped anywhere — chat,
// kernels view, eventually the activity surface.
//
// The widget owns no chat state. It re-runs whenever `kernel`,
// `params`, or `version` changes (the parent bumps `version` to
// force a manual refresh).

import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { KernelBlock } from "./KernelPrimitives.jsx";
import { runKernel } from "../lib/kernelRunner.js";
import { openKernelLink } from "../lib/kernelLinkNavigation.js";
import { kernelErrorMessage } from "../lib/kernelErrorCopy.js";
import { useMyPermissions } from "../lib/permissions.js";
import {
    CanvasRunScheduler,
    DEFAULT_KERNEL_RUN_TIMEOUT_MS,
} from "../lib/canvasRunScheduler.js";
import {
    KERNEL_RUN_CANCEL_REASON,
    KERNEL_RUN_OUTCOME,
} from "../lib/kernelRunOutcome.js";
import { asResolvedKernel } from "../lib/kernelResolver.js";
import { mapRunContext } from "../lib/kernelResolver.js";
import { withInteractiveConsent } from "../lib/runConsent.js";
import { useRunConsentBroker } from "./RunConsentProvider.jsx";
import KernelAuthorBadge from "./KernelAuthorBadge.jsx";
import { buttonClass } from "./ui/Button.jsx";

export default function KernelWidget({
    kernel,
    resolvedDefinition = null,
    runContext = null,
    params = null,
    version = 0,
    onOpenScope: onOpenScopeProp,
    onFocus = null,
    renderTransform = null,
    onDelete,
    runScheduler = null,
    executionPackageRequirements = null,
    runKey = null,
    runTimeoutMs = DEFAULT_KERNEL_RUN_TIMEOUT_MS,
    runMetadata = null,
    onRunStateChange = null,
    onRunOutcome = null,
    explicitRunToken = null,
    explicitRunTrigger = null,
    showHeader = true,
    // `bare` drops the widget's own frame (border + panel-soft bg + padding)
    // so a host that already provides a single card frame — the canvas card —
    // doesn't nest a second box around every widget.
    bare = false,
    // When the widget sits inside a fixed-height slot (activity
    // surface), `fill` lets the inner chart claim 100% of that slot.
    // In a free-flowing list (Kernels admin page) the parent has no
    // height to claim, so charts should render at their natural pixel
    // height — same shape kernel results take when inlined into chat.
    fill = true,
}) {
    const caps = useMyPermissions();
    const consentBroker = useRunConsentBroker();
    // Default link behavior: hand the click to App's navigation layer,
    // which lands in Explore from any mode (Studio included). Callers can
    // override by passing their own onOpenScope (e.g. for in-place
    // replacement instead of new-tab).
    const onOpenScope = onOpenScopeProp || openKernelLink;
    const [state, setState] = useState({ status: "queued" });
    // Capture the most recent run's identity so a late-completing
    // older run can't clobber the displayed state when the user
    // refreshed quickly. Compared by reference on the result.
    const runIdRef = useRef(0);
    const activeHandleRef = useRef(null);
    const unmountingRef = useRef(false);
    const onRunStateChangeRef = useRef(onRunStateChange);
    const onRunOutcomeRef = useRef(onRunOutcome);
    const consumedApprovalRef = useRef(null);
    const ownedSchedulerRef = useRef(null);
    const renderCommitRef = useRef(null);
    const nextRenderCommitIdRef = useRef(1);
    const [refreshNonce, setRefreshNonce] = useState(0);
    onRunStateChangeRef.current = onRunStateChange;
    onRunOutcomeRef.current = onRunOutcome;

    // A scheduled run is not complete until React has committed its output.
    // Resolving this barrier from a layout effect keeps the admission deadline
    // and concurrency slot active through KernelBlock/error rendering.
    useLayoutEffect(() => {
        const pending = renderCommitRef.current;
        if (!pending || state.renderCommitId !== pending.id) return;
        renderCommitRef.current = null;
        pending.resolve();
    }, [state]);

    const commitRunResult = useCallback((result, signal) => {
        if (signal?.aborted) {
            return Promise.reject(signal.reason || new Error("kernel run cancelled before render"));
        }
        const id = nextRenderCommitIdRef.current++;
        return new Promise((resolve, reject) => {
            let settled = false;
            const cleanup = () => {
                if (signal) signal.removeEventListener("abort", onAbort);
            };
            const settleResolve = () => {
                if (settled) return;
                settled = true;
                cleanup();
                resolve();
            };
            const onAbort = () => {
                if (settled) return;
                settled = true;
                cleanup();
                if (renderCommitRef.current?.id === id) {
                    renderCommitRef.current = null;
                }
                reject(signal.reason || new Error("kernel render cancelled"));
            };
            renderCommitRef.current = { id, resolve: settleResolve };
            if (signal) signal.addEventListener("abort", onAbort, { once: true });
            setState(kernelWidgetStateFromResult(result, id));
        });
    }, []);

    // Standalone widgets still get the same hard deadline and cancellation
    // semantics. Canvas hosts pass one shared scheduler so all cards share the
    // canvas-level FIFO/concurrency limit.
    const ownedScheduler = useMemo(
        () => runScheduler
            ? null
            : new CanvasRunScheduler({ concurrency: 1, timeoutMs: runTimeoutMs }),
        [runScheduler, runTimeoutMs],
    );
    const scheduler = runScheduler || ownedScheduler;
    ownedSchedulerRef.current = ownedScheduler;

    useEffect(() => {
        unmountingRef.current = false;
        return () => {
            unmountingRef.current = true;
        };
    }, []);

    useEffect(() => {
        if (!ownedScheduler) return undefined;
        return () => {
            queueMicrotask(() => {
                // React StrictMode immediately replays effects with the same
                // scheduler. Dispose only on a real unmount or replacement.
                if (unmountingRef.current || ownedSchedulerRef.current !== ownedScheduler) {
                    ownedScheduler.dispose({ code: KERNEL_RUN_CANCEL_REASON.UNMOUNTED });
                }
            });
        };
    }, [ownedScheduler]);

    const effectiveResolvedDefinition = useMemo(
        () => resolvedDefinition || asResolvedKernel(kernel),
        [kernel, resolvedDefinition],
    );

    const runIdentity = useMemo(() => ({
        kernelId: kernel?.id,
        resolvedDefinition: effectiveResolvedDefinition,
        runContext,
        params,
        version,
        refreshNonce,
        explicitRunToken,
        explicitRunTrigger,
        executionPackageRequirements,
    // params is intentionally treated as a reference dependency —
    // the parent owns it, and bumping `version` is the explicit
    // refresh signal anyway.
    }), [
        kernel?.id,
        effectiveResolvedDefinition,
        runContext,
        params,
        version,
        refreshNonce,
        explicitRunToken,
        explicitRunTrigger,
        executionPackageRequirements,
    ]);

    useEffect(() => {
        if (!kernel || !scheduler) return undefined;
        const myRunId = ++runIdRef.current;
        let effectActive = true;
        let handle = null;
        let committedResult = null;
        setState((cur) => ({
            ...cur,
            status: "queued",
            error: null,
            reason: null,
        }));

        // Deferring one microtask prevents React StrictMode's throwaway effect
        // pass from launching real backend/Python work.
        queueMicrotask(() => {
            if (!effectActive || runIdRef.current !== myRunId) return;
            let contextForAttempt = runContext;
            const approvalSource = explicitRunToken != null
                ? "external"
                : refreshNonce > 0
                    ? "widget-refresh"
                    : null;
            const approvalToken = explicitRunToken != null
                ? explicitRunToken
                : refreshNonce;
            const consumed = consumedApprovalRef.current;
            const isNewApproval = approvalSource
                && (
                    consumed?.source !== approvalSource
                    || !Object.is(consumed?.token, approvalToken)
                );
            if (isNewApproval) {
                // Consume before enqueue so the approval is bound to this
                // exact definition/params identity even if it waits in FIFO
                // and props change before the task launches.
                consumedApprovalRef.current = {
                    source: approvalSource,
                    token: approvalToken,
                };
                contextForAttempt = withInteractiveConsent(
                    mapRunContext(runContext, { agentStepApproved: true }),
                    {
                        broker: consentBroker,
                        triggerText: explicitRunTrigger
                            || `Refresh /${kernel?.command || kernel?.id || "kernel"}`,
                    },
                );
            }
            handle = scheduler.schedule(
                async ({ signal, runId, executionId }) => {
                    const result = await runKernel({
                        resolvedDefinition: effectiveResolvedDefinition,
                        runContext: contextForAttempt,
                        params: params || {},
                        signal,
                        runId,
                        executionId,
                        executionPackageRequirements,
                        releaseRealmOnComplete: false,
                        timeoutMs: 0,
                    });
                    if (signal.aborted) {
                        throw signal.reason || new Error("kernel run cancelled before render");
                    }
                    if (!effectActive || runIdRef.current !== myRunId) return result;
                    await commitRunResult(result, signal);
                    committedResult = result;
                    return result;
                },
                {
                    id: runKey || kernel.id || kernel.command || "kernel",
                    timeoutMs: runTimeoutMs,
                    metadata: runMetadata,
                    onStateChange: (event) => {
                        onRunStateChangeRef.current?.(event);
                        if (runIdRef.current !== myRunId) return;
                        if (["queued", "running", "cancelling"].includes(event.state)) {
                            setState((cur) => ({
                                ...cur,
                                status: event.state,
                                reason: event.reason || cur.reason || null,
                            }));
                        }
                    },
                    onOutcome: (event) => {
                        onRunOutcomeRef.current?.({
                            ...event,
                            kernel,
                            resolvedDefinition: effectiveResolvedDefinition,
                        });
                    },
                },
            );
            activeHandleRef.current = handle;

            handle.promise.then((res) => {
                if (runIdRef.current !== myRunId) return;
                if (activeHandleRef.current === handle) activeHandleRef.current = null;
                // Queued cancellation/expiry never enters the task, while a
                // cancellation racing render rejects its commit barrier.
                // Normal results were already committed inside the scheduled
                // task so its deadline includes React render/commit.
                if (committedResult !== res) setState(kernelWidgetStateFromResult(res));
            }).catch((err) => {
                if (runIdRef.current !== myRunId) return;
                if (activeHandleRef.current === handle) activeHandleRef.current = null;
                setState({
                    status: "error",
                    error: err,
                    ranAt: Date.now(),
                });
            });
        });

        return () => {
            effectActive = false;
            handle?.cancel({
                code: unmountingRef.current
                    ? KERNEL_RUN_CANCEL_REASON.UNMOUNTED
                    : KERNEL_RUN_CANCEL_REASON.SUPERSEDED,
            });
        };
    }, [
        kernel,
        effectiveResolvedDefinition,
        runContext,
        runIdentity,
        runKey,
        runMetadata,
        runTimeoutMs,
        scheduler,
        executionPackageRequirements,
        consentBroker,
        explicitRunToken,
        explicitRunTrigger,
        refreshNonce,
        commitRunResult,
    ]);

    const refresh = useCallback(() => {
        activeHandleRef.current?.cancel({
            code: KERNEL_RUN_CANCEL_REASON.SUPERSEDED,
        });
        setRefreshNonce((nonce) => nonce + 1);
    }, []);

    const projectedRender = useMemo(() => {
        if (state.status !== "done" || !renderTransform) {
            return {
                status: "ready",
                data: state.renderData,
                spec: state.renderSpec,
                issues: [],
            };
        }
        try {
            return renderTransform(state.renderData, state.renderSpec) || {
                status: "ready",
                data: state.renderData,
                spec: state.renderSpec,
                issues: [],
            };
        } catch (error) {
            return {
                status: "conflict",
                data: state.renderData,
                spec: state.renderSpec,
                issues: [{
                    message: error?.message || "The Canvas projection could not be applied.",
                }],
            };
        }
    }, [renderTransform, state.renderData, state.renderSpec, state.status]);

    const stop = useCallback(() => {
        const handle = activeHandleRef.current;
        handle?.cancel({
            code: handle.state === "queued"
                ? KERNEL_RUN_CANCEL_REASON.QUEUED_CANCELLED
                : KERNEL_RUN_CANCEL_REASON.USER_STOP,
        });
    }, []);

    if (!kernel) return null;

    const frameCls = bare
        ? ""
        : " rounded-[10px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] p-[10px]";
    const outerCls = (fill
        ? "flex h-full min-h-0 flex-col gap-[6px]"
        : "flex flex-col gap-[6px]") + frameCls;
    const bodyCls = fill ? "flex flex-1 min-h-0 flex-col" : "flex flex-col";
    const pending = ["queued", "running", "cancelling"].includes(state.status);
    return (
        <div className={outerCls} aria-busy={pending ? "true" : undefined}>
            {showHeader ? (
                <div className="flex min-w-0 items-baseline gap-[8px]">
                    <div className="min-w-0 flex-1">
                        <KernelWidgetHeader
                            kernel={kernel}
                            state={state}
                            onRefresh={refresh}
                            onStop={stop}
                            onDelete={onDelete}
                        />
                    </div>
                    <KernelAuthorBadge
                        resolvedDefinition={resolvedDefinition || kernel}
                        record
                        onlyWhenChanged={bare}
                    />
                </div>
            ) : (
                <div
                    data-export-ignore="true"
                    className="flex min-h-[12px] shrink-0 justify-end"
                >
                    <KernelAuthorBadge
                        resolvedDefinition={resolvedDefinition || kernel}
                        record
                        onlyWhenChanged={bare}
                    />
                </div>
            )}
            <div className={bodyCls}>
                {!pending
                    && ["done", "empty"].includes(state.status)
                    && state.scopeWarnings?.length ? (
                        <div
                            data-export-ignore="true"
                            className="mb-[6px] rounded-[7px] border border-[color:var(--line)] bg-[color:var(--panel)] px-[6px] py-[3px] text-[10.5px] leading-[1.3] text-[color:var(--muted)]"
                            title={state.scopeWarnings.map((w) => w.reason).join("; ")}
                        >
                            Showing tenant-wide data. This kernel does not use the active scope.
                        </div>
                    ) : null}
                {pending ? (
                    <KernelWidgetRunning
                        kernel={kernel}
                        status={state.status}
                        onStop={!showHeader ? stop : null}
                    />
                ) : state.status === "cancelled" ? (
                    <KernelWidgetCancelled reason={state.reason} />
                ) : state.status === "empty" ? (
                    <KernelWidgetEmpty message={state.renderSpec?.empty} />
                ) : ["refused", "unavailable", "error"].includes(state.status) ? (
                    <KernelWidgetError
                        error={state.error}
                        caps={caps}
                        steps={state.stepSummaries}
                        label={state.status === "error" ? "failed" : state.status}
                        presentation={bare}
                        onRun={
                            !showHeader
                            && state.status === "refused"
                            && state.reason?.reasonCode === "agent-click-required"
                                ? refresh
                                : null
                        }
                    />
                ) : (
                    projectedRender.status === "ready" ? (
                        <KernelBlock
                            spec={projectedRender.spec}
                            data={projectedRender.data}
                            onOpenScope={onOpenScope}
                            onFocus={onFocus}
                            fillHeight={fill}
                        />
                    ) : (
                        <KernelProjectionNotice projection={projectedRender} />
                    )
                )}
            </div>
        </div>
    );
}

function KernelProjectionNotice({ projection }) {
    const label = projection.status === "schema-unavailable"
        ? "Schema unavailable"
        : projection.status === "unsupported"
            ? "Unsupported Canvas runtime"
            : "Canvas runtime conflict";
    return (
        <div
            role="status"
            className="rounded-[7px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[8px] py-[6px] text-[10.5px] leading-[1.35] text-[color:var(--muted)]"
        >
            <span className="mr-[6px] font-medium text-[color:var(--ink)]">
                {label}
            </span>
            {projection.issues?.[0]?.message || "This Canvas interaction cannot be applied safely."}
        </div>
    );
}

function kernelWidgetStateFromResult(res, renderCommitId = null) {
    const committed = renderCommitId == null ? {} : { renderCommitId };
    const ranAt = Date.now();
    if (res?.outcome === KERNEL_RUN_OUTCOME.EMPTY) {
        return {
            status: "empty",
            renderSpec: res.renderSpec || null,
            scopeWarnings: res.scopeWarnings || null,
            ranAt,
            ...committed,
        };
    }
    if (res?.ok) {
        return {
            status: "done",
            renderSpec: res.renderSpec,
            renderData: res.renderData,
            stepSummaries: res.stepSummaries || null,
            scopeWarnings: res.scopeWarnings || null,
            params: res.params || null,
            ranAt,
            ...committed,
        };
    }
    if (res?.cancelled || res?.outcome === KERNEL_RUN_OUTCOME.CANCELLED) {
        return {
            status: "cancelled",
            error: res.error || null,
            reason: res.reason || null,
            stepSummaries: res.stepSummaries || null,
            ranAt,
            ...committed,
        };
    }
    if (res?.outcome === KERNEL_RUN_OUTCOME.REFUSED) {
        return {
            status: "refused",
            error: res.error || new Error("kernel run refused"),
            reason: res.refusal || null,
            stepSummaries: res.stepSummaries || null,
            ranAt,
            ...committed,
        };
    }
    if (res?.outcome === KERNEL_RUN_OUTCOME.UNAVAILABLE) {
        return {
            status: "unavailable",
            error: res.error || new Error("kernel unavailable"),
            reason: res.reason || null,
            stepSummaries: res.stepSummaries || null,
            ranAt,
            ...committed,
        };
    }
    return {
        status: "error",
        error: res?.error || new Error("kernel failed"),
        stepSummaries: res?.stepSummaries || null,
        ranAt,
        ...committed,
    };
}

function KernelWidgetHeader({ kernel, state, onRefresh, onStop, onDelete }) {
    const ago = state.ranAt ? formatAgo(state.ranAt) : null;
    const pending = ["queued", "running", "cancelling"].includes(state.status);
    const dotColor =
        state.status === "done" ? "var(--green)"
        : ["error", "unavailable"].includes(state.status) ? "var(--red, #c0392b)"
        : "var(--ink)";
    return (
        <div data-export-ignore="true" className="flex items-baseline gap-[8px] text-[10.5px] leading-[1.3]">
            <span
                className={`relative top-[1px] inline-block h-[7px] w-[7px] shrink-0 rounded-full ${pending ? "animate-pulse" : ""}`}
                style={{ background: dotColor }}
                aria-hidden="true"
            />
            <span className="shrink-0 whitespace-nowrap font-mono text-[color:var(--ink)]">
                /{kernel.command}
            </span>
            <span className="min-w-0 flex-1 truncate text-[color:var(--muted)]">
                {kernel.title || kernel.command}
            </span>
            {ago ? (
                <span className="shrink-0 whitespace-nowrap text-[color:var(--muted-soft)]">
                    {ago}
                </span>
            ) : null}
            {pending ? (
                <button
                    type="button"
                    onClick={onStop}
                    title={state.status === "queued" ? "Cancel queued kernel" : "Stop kernel"}
                    className="shrink-0 cursor-pointer rounded-[5px] border border-[color:var(--line)] px-[6px] py-[1px] text-[11px] capitalize text-[color:var(--muted)] hover:border-[color:var(--red,#c0392b)] hover:text-[color:var(--red,#c0392b)]"
                >
                    {state.status === "queued" ? "cancel" : "stop"}
                </button>
            ) : (
                <button
                    type="button"
                    onClick={onRefresh}
                    title="Re-run kernel"
                    className="shrink-0 cursor-pointer rounded-[5px] border border-[color:var(--line)] px-[6px] py-[1px] text-[11px] capitalize text-[color:var(--muted)] hover:border-[color:var(--ink)] hover:text-[color:var(--ink)]"
                >
                    refresh
                </button>
            )}
            {onDelete ? (
                <button
                    type="button"
                    onClick={onDelete}
                    title="Delete this kernel"
                    className="shrink-0 cursor-pointer rounded-[5px] border border-[color:var(--line)] px-[6px] py-[1px] text-[11px] capitalize text-[color:var(--muted)] hover:border-[color:var(--red,#c0392b)] hover:text-[color:var(--red,#c0392b)]"
                >
                    delete
                </button>
            ) : null}
        </div>
    );
}

function KernelWidgetRunning({ kernel, status, onStop }) {
    const stepCount = Array.isArray(kernel.steps) ? kernel.steps.length : 0;
    const label =
        status === "queued" ? "queued"
        : status === "cancelling" ? "stopping"
        : `running ${stepCount} step${stepCount === 1 ? "" : "s"}`;
    return (
        <div className="grid flex-1 min-h-[120px] place-items-center gap-[8px] text-[11px] text-[color:var(--muted-soft)]">
            <span>{label}…</span>
            {onStop ? (
                <button
                    type="button"
                    onClick={onStop}
                    className={`${buttonClass("secondary", "sm")} capitalize`}
                >
                    {status === "queued" ? "cancel" : "stop"}
                </button>
            ) : null}
        </div>
    );
}

function KernelWidgetCancelled({ reason }) {
    return (
        <div className="rounded-[7px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] px-[10px] py-[8px] text-[11px] leading-[1.4] text-[color:var(--muted)]">
            <div className="mb-[2px] text-[12px] text-[color:var(--muted)] first-letter:uppercase">
                cancelled
            </div>
            <div className="[overflow-wrap:anywhere]">
                {reason?.message || "Run cancelled."}
            </div>
        </div>
    );
}

function KernelWidgetEmpty({ message }) {
    const copy = typeof message === "string" && message.trim()
        ? message
        : "No data matched this view in your current scope.";
    return (
        <div className="rounded-[7px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] px-[10px] py-[8px] text-[11px] leading-[1.4] text-[color:var(--muted)]">
            <div className="mb-[2px] text-[12px] text-[color:var(--muted)] first-letter:uppercase">
                empty
            </div>
            <div>{copy}</div>
        </div>
    );
}

function KernelWidgetError({
    error,
    caps,
    steps,
    label = "failed",
    presentation = false,
    onRun = null,
}) {
    const message = presentation
        ? label === "refused" && onRun
            ? "Run this view to continue."
            : label === "refused"
                ? "You do not have access to this view."
                : label === "unavailable"
                    ? "This view is unavailable right now. Refresh to retry."
                    : "This view could not load. Refresh to retry."
        : kernelErrorMessage(error, caps);
    return (
        <div className="space-y-[6px]">
            <div className="rounded-[7px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] px-[10px] py-[8px] text-[11px] leading-[1.4] text-[color:var(--muted)]">
                <div className="mb-[2px] text-[12px] text-[color:var(--muted)] first-letter:uppercase">
                    {label}
                </div>
                <div className="[overflow-wrap:anywhere]">{message}</div>
                {onRun ? (
                    <button
                        type="button"
                        onClick={onRun}
                        data-export-ignore="true"
                        className={`${buttonClass("primary", "sm")} mt-[7px] capitalize`}
                    >
                        run
                    </button>
                ) : null}
            </div>
            {!presentation && Array.isArray(steps) && steps.length ? (
                <div className="space-y-[2px] text-[10.5px] leading-[1.4] text-[color:var(--muted)]">
                    <div className="text-[12px] text-[color:var(--muted)] first-letter:uppercase">
                        steps before failure
                    </div>
                    {steps.map((s, i) => (
                        <div key={s.id || i} className="flex items-baseline gap-[6px]">
                            <span className="shrink-0 whitespace-nowrap font-mono text-[color:var(--ink)]">
                                {s.id || `step${i}`}
                            </span>
                            <span className="shrink-0 whitespace-nowrap text-[color:var(--muted-soft)]">
                                {s.kind}
                            </span>
                            <span className="min-w-0 flex-1 truncate">{s.shape}</span>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function formatAgo(ts) {
    if (!ts) return "";
    const diff = Date.now() - ts;
    if (diff < 5000) return "just now";
    if (diff < 60_000) return `${Math.round(diff / 1000)}s ago`;
    if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
    return `${Math.round(diff / 86_400_000)}d ago`;
}
